import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const WorkflowStage = ({ 
  stage, 
  jobs, 
  onJobClick, 
  onAssignOperator, 
  onRecordCompletion,
  onDragStart,
  onDragOver,
  onDrop 
}) => {
  const getStageIcon = (stageName) => {
    switch (stageName) {
      case 'Digital Printing': return 'Printer';
      case 'Cutting': return 'Scissors';
      case 'Stitching': return 'Shirt';
      default: return 'Package';
    }
  };

  const getStageColor = (stageName) => {
    switch (stageName) {
      case 'Digital Printing': return 'bg-blue-50 border-blue-200';
      case 'Cutting': return 'bg-orange-50 border-orange-200';
      case 'Stitching': return 'bg-green-50 border-green-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    onDragOver && onDragOver(e, stage.id);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    onDrop && onDrop(e, stage.id);
  };

  return (
    <div 
      className={`flex-1 min-h-[600px] border-2 border-dashed rounded-lg p-4 ${getStageColor(stage.name)}`}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Stage Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-elevation-1">
            <Icon name={getStageIcon(stage.name)} size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{stage.name}</h3>
            <p className="text-sm text-muted-foreground">{jobs.length} jobs pending</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="text-right">
            <p className="text-sm font-medium text-foreground">{stage.completionRate}%</p>
            <p className="text-xs text-muted-foreground">Complete</p>
          </div>
          <div className="w-12 h-12 relative">
            <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 36 36">
              <path
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="var(--color-muted)"
                strokeWidth="2"
              />
              <path
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="var(--color-primary)"
                strokeWidth="2"
                strokeDasharray={`${stage.completionRate}, 100`}
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Assigned Operators */}
      <div className="mb-4 p-3 bg-white rounded-lg shadow-elevation-1">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-foreground">Assigned Operators</h4>
          <Button
            variant="ghost"
            size="xs"
            iconName="Plus"
            iconSize={14}
            onClick={() => onAssignOperator(stage.id)}
          >
            Assign
          </Button>
        </div>
        <div className="space-y-2">
          {stage.operators.map((operator) => (
            <div key={operator.id} className="flex items-center space-x-2">
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                <span className="text-xs text-primary-foreground font-medium">
                  {operator.name.charAt(0)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{operator.name}</p>
                <p className="text-xs text-muted-foreground">{operator.activeJobs} active jobs</p>
              </div>
              <div className={`w-2 h-2 rounded-full ${
                operator.status === 'available' ? 'bg-success' : 
                operator.status === 'busy' ? 'bg-warning' : 'bg-error'
              }`}></div>
            </div>
          ))}
        </div>
      </div>

      {/* Job Cards */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {jobs.map((job) => (
          <div
            key={job.id}
            draggable
            onDragStart={(e) => onDragStart && onDragStart(e, job)}
            className="bg-white rounded-lg p-4 shadow-elevation-1 border border-border cursor-move hover:shadow-elevation-2 transition-smooth"
            onClick={() => onJobClick(job)}
          >
            {/* Job Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${
                  job.priority === 'high' ? 'bg-error' :
                  job.priority === 'medium' ? 'bg-warning' : 'bg-success'
                }`}></div>
                <h5 className="font-medium text-foreground">{job.designName}</h5>
              </div>
              <span className="text-xs text-muted-foreground">#{job.jobId}</span>
            </div>

            {/* Job Details */}
            <div className="space-y-2 mb-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Fabric Required:</span>
                <span className="font-medium text-foreground">{job.fabricMeters}m</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Total Pieces:</span>
                <span className="font-medium text-foreground">{job.totalPieces}</span>
              </div>
              {job.assignedOperator && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Assigned to:</span>
                  <span className="font-medium text-foreground">{job.assignedOperator}</span>
                </div>
              )}
            </div>

            {/* Size Distribution */}
            <div className="mb-3">
              <p className="text-xs text-muted-foreground mb-2">Size Distribution:</p>
              <div className="flex space-x-1">
                {Object.entries(job.sizeDistribution).map(([size, count]) => (
                  <div key={size} className="flex-1 text-center">
                    <div className="bg-muted rounded px-1 py-0.5">
                      <p className="text-xs font-medium text-foreground">{size}</p>
                      <p className="text-xs text-muted-foreground">{count}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Progress and Timeline */}
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Progress</span>
                  <span className="font-medium text-foreground">{job.progress}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-1.5">
                  <div 
                    className="bg-primary h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${job.progress}%` }}
                  ></div>
                </div>
              </div>
              <div className="ml-3 text-right">
                <p className="text-xs text-muted-foreground">Due Date</p>
                <p className="text-xs font-medium text-foreground">{job.dueDate}</p>
              </div>
            </div>

            {/* Defects (for Cutting stage) */}
            {stage.name === 'Cutting' && job.defects > 0 && (
              <div className="mt-2 p-2 bg-error/10 rounded border border-error/20">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertTriangle" size={14} color="var(--color-error)" />
                  <span className="text-xs text-error font-medium">{job.defects} defective pieces</span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-2 mt-3 pt-3 border-t border-border">
              {job.progress === 100 ? (
                <Button
                  variant="success"
                  size="xs"
                  fullWidth
                  iconName="Check"
                  iconSize={14}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRecordCompletion(job.id);
                  }}
                >
                  Mark Complete
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="xs"
                    iconName="Play"
                    iconSize={14}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Start
                  </Button>
                  <Button
                    variant="ghost"
                    size="xs"
                    iconName="Pause"
                    iconSize={14}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Pause
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {jobs.length === 0 && (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
            <Icon name={getStageIcon(stage.name)} size={24} color="var(--color-muted-foreground)" />
          </div>
          <h4 className="font-medium text-foreground mb-2">No jobs in {stage.name}</h4>
          <p className="text-sm text-muted-foreground">Jobs will appear here when assigned to this stage</p>
        </div>
      )}
    </div>
  );
};

export default WorkflowStage;